import React, { FC, useEffect, useState, useMemo } from 'react';
import {
  Button,
  Chip,
  IconButton,
  Skeleton,
  Box,
  Typography,
  Drawer,
  Paper,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Table, TableBody, TableCell, TableHead, TableRow, tableCellClasses } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  WorkOutline as WorkIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchSalaries, deleteSalary, paySalary, payDailyWage } from '../../store/salaryStore/salaryThunk';
import { fetchEmployees } from '../../store/employeeStore/employeeThunk';
import { ApiStatus } from '../../utils/api.types';
import { SalaryPayment } from '../../utils/salary.types';
import { Utils } from '../../utils/common-utils';
import styled_ from "@emotion/styled";
import DialogProcessSalaryForm from '../../components/Salary/DialogProcessSalaryForm';
import DialogProcessDailyWageForm from '../../components/Salary/DialogProcessDailyWageForm';
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import generatePDF, { Resolution, Margin, Options } from "react-to-pdf";
import axios from '../../utils/axios';

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#fff',
    color: '#000',
    fontSize: '0.80rem',
    fontWeight: '700',
    borderRight: '2px solid #F5F5F5',
    padding: '12px 16px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '0.975rem',
    fontWeight: '400',
    color: '#292d35',
    padding: '8px 16px',
    borderBottom: 'none',
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: '#f9fafb',
  },
  '& > *': {
    borderBottom: '0px',
  }
}));

const SalaryList: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { salaries, status } = useAppSelector(state => state.salaries);
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { employees } = useAppSelector(state => state.employees);

  const [openSalaryDrawer, setOpenSalaryDrawer] = useState(false);
  const [openDailyWageDrawer, setOpenDailyWageDrawer] = useState(false);
  const [isRefresh, setIsRefresh] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [salaryToDelete, setSalaryToDelete] = useState<SalaryPayment | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Filter states
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());

  // Filter salaries by type
  const monthlySalaries = useMemo(() =>
    salaries?.filter((s: any) => s.paymentType === 'MONTHLY_SALARY') || [],
    [salaries]
  );

  const dailyWages = useMemo(() =>
    salaries?.filter((s: any) => s.paymentType === 'DAILY_WAGE') || [],
    [salaries]
  );

  const currentSalaries = tabValue === 0 ? monthlySalaries : dailyWages;

  useEffect(() => {
    if (selectedAccount?._id) {
      // Fetch employees for filter dropdown
      dispatch(fetchEmployees({ accountId: selectedAccount._id, type: 'all' }));
    }
  }, [dispatch, selectedAccount]);

  useEffect(() => {
    // Debounce the API call when year is being typed
    const timeoutId = setTimeout(() => {
      if (selectedAccount?._id) {
        const filters: any = {
          accountId: selectedAccount._id
        };

        if (selectedEmployee) filters.employee = selectedEmployee;

        // Only apply month/year filters for Monthly Salary tab (tabValue === 0)
        if (tabValue === 0) {
          // If both month and year are selected, create YYYY-MM format
          if (selectedMonth && selectedYear && selectedYear.length === 4) {
            filters.month = `${selectedYear}-${selectedMonth}`;
          } else if (selectedYear && selectedYear.length === 4) {
            // If only year is selected, filter by year (only when 4 digits are entered)
            filters.year = selectedYear;
          }
        }

        dispatch(fetchSalaries(filters));
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [dispatch, selectedAccount, isRefresh, selectedEmployee, selectedMonth, selectedYear, tabValue]);

  const handleRefresh = () => {
    setIsRefresh(Date.now().toString());
  };

  const handleOpenSalary = () => setOpenSalaryDrawer(true);
  const handleOpenDailyWage = () => setOpenDailyWageDrawer(true);

  const handleCloseSalary = async (val: any) => {
    if (val) {
      handleRefresh();
    }
    setOpenSalaryDrawer(false);
  };

  const handleCloseDailyWage = async (val: any) => {
    if (val) {
      handleRefresh();
    }
    setOpenDailyWageDrawer(false);
  };

  const handleDeleteSalary = async (salaryId: string) => {
    try {
      await dispatch(deleteSalary(salaryId)).unwrap();
      handleRefresh();
    } catch (error) {
      console.error('Failed to delete salary:', error);
    }
  };

  const handleMarkAsPaid = async (salary: SalaryPayment) => {
    try {
      const paymentMode = salary.paymentMode || 'CASH';

      const paymentData: any = {
        id: salary._id,
        paymentDate: new Date(),
        paymentMode: paymentMode,
      };

      // Only include fromBankAccount if payment mode is not CASH
      if (paymentMode !== 'CASH' && salary.fromBankAccount) {
        // Extract ID if fromBankAccount is a populated object
        paymentData.fromBankAccount = typeof salary.fromBankAccount === 'string'
          ? salary.fromBankAccount
          : (salary.fromBankAccount as any)._id;
      }

      // Call appropriate API based on payment type
      if (salary.paymentType === 'DAILY_WAGE') {
        await dispatch(payDailyWage(paymentData)).unwrap();
      } else {
        await dispatch(paySalary(paymentData)).unwrap();
      }

      handleRefresh();
    } catch (error) {
      console.error('Failed to mark as paid:', error);
      alert('Failed to mark as paid. Please try again.');
    }
  };

  // Delete dialog handlers
  const handleDeleteClick = (salary: SalaryPayment) => {
    setSalaryToDelete(salary);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (salaryToDelete) {
      await handleDeleteSalary(salaryToDelete._id);
    }
    setDeleteDialogOpen(false);
    setSalaryToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSalaryToDelete(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Download salary slip PDF
  const handleDownloadSlip = async (salaryId: string, employeeName: string) => {
    try {
      const response = await axios.get(`/api/v1/salary/${salaryId}/download-slip`, {
        responseType: 'blob'
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Salary_Slip_${employeeName.replace(/\s+/g, '_')}_${moment().format('YYYY_MM_DD')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download salary slip:', error);
      alert('Failed to download salary slip. Please try again.');
    }
  };

  // Export to Excel
  const exportToExcel = (): void => {
    const exportData = currentSalaries.map((salary: any) => ({
      Date: moment(salary.createdAt).format("DD MMM YYYY"),
      'TXN ID': salary.txnId,
      Employee: salary.employee?.name || '-',
      'Emp ID': salary.employee?.empId || '-',
      Type: salary.paymentType === 'MONTHLY_SALARY' ? 'Monthly' : 'Daily Wage',
      Period: salary.paymentType === 'MONTHLY_SALARY'
        ? (salary.paymentPeriod?.month || '-')
        : `${moment(salary.paymentPeriod?.fromDate).format('DD/MM/YY')} - ${moment(salary.paymentPeriod?.toDate).format('DD/MM/YY')}`,
      'Gross Amount': parseFloat(salary.grossSalary || 0).toFixed(2),
      Deductions: parseFloat(salary.deductions?.totalDeductions || 0).toFixed(2),
      'Net Payable': parseFloat(salary.netSalary || 0).toFixed(2),
      Status: salary.paymentStatus,
    }));

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const workbook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Salary Data");

    const excelBuffer: any = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const dataBlob: Blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

    saveAs(dataBlob, `Salary_List_${moment().format('YYYYMMDD')}.xlsx`);
  };

  // PDF export options
  const options: Options = {
    filename: `Salary_Statement_${Date.now()}.pdf`,
    method: "save",
    resolution: Resolution.NORMAL,
    page: {
      margin: Margin.MEDIUM,
      format: "A4",
    },
    canvas: {
      mimeType: "image/jpeg",
      qualityRatio: 1
    },
    overrides: {
      pdf: {
        compress: true
      },
      canvas: {
        useCORS: true
      }
    }
  };

  const getTargetElement = () => document.getElementById("pdfContainer");

  const downloadPdf = () => {
    generatePDF(getTargetElement, options);
  };

  if (status === ApiStatus.LOADING && !salaries?.length) {
    return (
      <Box p={3}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  return (
    <>
    <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', backgroundColor: '#fff' }}>
      {/* Header Bar with Actions */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
 
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#fafafa'
      }}>
      
        <Box sx={{ borderBottom: 1, borderColor: 'divider', }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="Salary Tabs">
            <Tab label="Monthly Salary" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="Daily Wages" sx={{ textTransform: 'none', fontWeight: 600 }} />
          </Tabs>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<WorkIcon />}
            onClick={handleOpenDailyWage}
            variant="outlined"
            color='secondary'
            size="small"
            sx={{ height: '36px', textTransform: 'none' }}
          >
            Process Daily Wage
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={handleOpenSalary}
            variant="contained"
            color='secondary'
            size="small"
            sx={{ height: '36px', textTransform: 'none' , mr: 2}}
          >
            Process Salary
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider',  display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="employee-filter-label" shrink={true}>Employee</InputLabel>
          <Select
            labelId="employee-filter-label"
            value={selectedEmployee}
            label="Employee"
            onChange={(e) => setSelectedEmployee(e.target.value)}
            displayEmpty
            notched={true}
          >
            <MenuItem value="">All Employees</MenuItem>
            {employees?.map((emp: any) => (
              <MenuItem key={emp._id} value={emp._id}>
                {emp.name} ({emp.empId})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Show Month and Year filters only for Monthly Salary tab */}
        {tabValue === 0 && (
          <>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="month-filter-label" shrink={true}>Month</InputLabel>
              <Select
                labelId="month-filter-label"
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
                displayEmpty
                notched={true}
              >
                <MenuItem value="">All Months</MenuItem>
                <MenuItem value="01">January</MenuItem>
                <MenuItem value="02">February</MenuItem>
                <MenuItem value="03">March</MenuItem>
                <MenuItem value="04">April</MenuItem>
                <MenuItem value="05">May</MenuItem>
                <MenuItem value="06">June</MenuItem>
                <MenuItem value="07">July</MenuItem>
                <MenuItem value="08">August</MenuItem>
                <MenuItem value="09">September</MenuItem>
                <MenuItem value="10">October</MenuItem>
                <MenuItem value="11">November</MenuItem>
                <MenuItem value="12">December</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Year"
              type="number"
              size="small"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ width: 120 }}
              inputProps={{ min: 2020, max: 2050 }}
            />
          </>
        )}

        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setSelectedEmployee('');
            setSelectedMonth('');
            setSelectedYear(new Date().getFullYear().toString());
          }}
          sx={{ height: '40px' }}
        >
          Clear Filters
        </Button>
      </Box>

      {/* Table Content */}
      <Box sx={{ p: 0, pb: 3 }}>
        <div id='pdfContainer'>
          <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: 'none', overflow: 'hidden' }}>
            <Table aria-label="salary table">
              <TableHead>
                <TableRow>
                  <TableCell align="left" className='tableHead' sx={{ fontSize: '0.80rem', fontWeight: '700', borderRight: '2px solid #F5F5F5' }}>Date</TableCell>
                  <TableCell align="left" className='tableHead' sx={{ fontSize: '0.80rem', fontWeight: '700', borderRight: '2px solid #F5F5F5', width: '18%' }}>Employee</TableCell>
                  {tabValue === 0 ? (
                    // Monthly Salary Columns
                    <>
                      <TableCell align="left" className='tableHead' sx={{ fontSize: '0.80rem', fontWeight: '700', borderRight: '2px solid #F5F5F5' }}>Period</TableCell>
                      <TableCell align="right" className='tableHead' sx={{ fontSize: '0.80rem', fontWeight: '700', borderRight: '2px solid #F5F5F5' }}>Gross Amount</TableCell>
                      <TableCell align="right" className='tableHead' sx={{ fontSize: '0.80rem', fontWeight: '700', borderRight: '2px solid #F5F5F5' }}>Deductions</TableCell>
                    </>
                  ) : (
                    // Daily Wage Columns
                    <>
                      <TableCell align="left" className='tableHead' sx={{ fontSize: '0.80rem', fontWeight: '700', borderRight: '2px solid #F5F5F5' }}>Period</TableCell>
                      <TableCell align="left" className='tableHead' sx={{ fontSize: '0.80rem', fontWeight: '700', borderRight: '2px solid #F5F5F5' }}>Rate/Days</TableCell>
                      <TableCell align="right" className='tableHead' sx={{ fontSize: '0.80rem', fontWeight: '700', borderRight: '2px solid #F5F5F5' }}>Gross Wage</TableCell>
                    </>
                  )}
                  <TableCell align="right" className='tableHead' sx={{ fontSize: '0.80rem', fontWeight: '700', borderRight: '2px solid #F5F5F5' }}>Net Payable</TableCell>
                  <TableCell align="left" className='tableHead' sx={{ fontSize: '0.80rem', fontWeight: '700', borderRight: '2px solid #F5F5F5' }}>Status</TableCell>
                  <TableCell align="center" className='tableHead' sx={{ fontSize: '0.80rem', fontWeight: '700', borderRight: '2px solid #F5F5F5' }}>Action</TableCell>
                </TableRow>
              </TableHead>
                    <TableBody>
                      {(currentSalaries && currentSalaries.length > 0) ? currentSalaries.map((row: any, index: any) => (
                        <TableRow key={index} sx={{ '& > *': { borderBottom: '0px', fontSize: '0.975rem', fontWeight: '400' } }}>
                          <TableCell className="tableRowCell" sx={{ fontSize: '0.975rem', fontWeight: '400', borderBottom: 'none' }}>
                            {row.createdAt ? (
                              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  <span style={{ fontWeight: '500', fontSize: '9pt' }}>
                                    {moment(row.createdAt).format("DD MMM YYYY")}
                                  </span>
                                </div>
                                <span style={{ fontSize: '7pt', color: '#6b7280' }}>
                                  {moment(row.createdAt).format("hh:mm A")}
                                </span>
                                <Chip
                                  label={`TXN #${row.txnId}`}
                                  size="small"
                                  style={{
                                    fontSize: '7pt',
                                    height: '18px',
                                    backgroundColor: '#7c2d12',
                                    color: 'white',
                                    marginTop: '4px'
                                  }}
                                />
                              </div>
                            ) : '-'}
                          </TableCell>

                          <TableCell align="left" className="tableRowCell" sx={{ fontSize: '0.975rem', fontWeight: '400', borderBottom: 'none' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                              <span style={{ fontWeight: '500', fontSize: '9pt' }}>
                                {row.employee?.name || '-'}
                              </span>
                              <span style={{ fontSize: '8pt', color: '#6b7280' }}>
                                {row.employee?.empId || ''}
                              </span>
                            </div>
                          </TableCell>

                          {/* Period Column */}
                          <TableCell className="tableRowCell" sx={{ fontSize: '0.975rem', fontWeight: '400', borderBottom: 'none' }}>
                            {row.paymentType === 'MONTHLY_SALARY' ? (
                              row.paymentPeriod?.month || '-'
                            ) : (
                              <span style={{ fontSize: '8pt' }}>
                                {moment(row.paymentPeriod?.fromDate).format('DD/MM/YY')} - {moment(row.paymentPeriod?.toDate).format('DD/MM/YY')}
                              </span>
                            )}
                          </TableCell>

                          {tabValue === 0 ? (
                            // Monthly Salary Columns
                            <>
                              <TableCell align="right" className="tableRowCell" sx={{ fontSize: '0.975rem', fontWeight: '400', borderBottom: 'none' }}>
                                <p className="flex-end">{row.grossSalary > 0 ? Utils.formatNumber(parseFloat(row.grossSalary).toFixed(2), true, true) : '-'}</p>
                              </TableCell>
                              <TableCell align="right" className="tableRowCell" sx={{ fontSize: '0.975rem', fontWeight: '400', borderBottom: 'none' }}>
                                <p className="flex-end">{row.deductions?.totalDeductions > 0 ? Utils.formatNumber(parseFloat(row.deductions.totalDeductions).toFixed(2), true, true) : '-'}</p>
                              </TableCell>
                            </>
                          ) : (
                            // Daily Wage Columns
                            <>
                              <TableCell align="left" className="tableRowCell" sx={{ fontSize: '0.975rem', fontWeight: '400', borderBottom: 'none' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
                                  <span style={{ fontSize: '8pt', color: '#6b7280' }}>
                                    {row.dailyWage?.wageCalculationMethod === 'PER_DAY'
                                      ? `₹${row.dailyWage?.ratePerDay || 0}/day × ${row.dailyWage?.daysWorked || 0} days`
                                      : `₹${row.dailyWage?.ratePerHour || 0}/hr × ${row.dailyWage?.hoursWorked || 0} hrs`
                                    }
                                  </span>
                                  {row.dailyWage?.overtimeHours > 0 && (
                                    <span style={{ fontSize: '7pt', color: '#059669' }}>
                                      + OT: {row.dailyWage.overtimeHours}hrs @ ₹{row.dailyWage.overtimeRate}/hr
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell align="right" className="tableRowCell" sx={{ fontSize: '0.975rem', fontWeight: '400', borderBottom: 'none' }}>
                                <p className="flex-end">{row.grossSalary > 0 ? Utils.formatNumber(parseFloat(row.grossSalary).toFixed(2), true, true) : '-'}</p>
                              </TableCell>
                            </>
                          )}

                          <TableCell align="right" className="tableRowCell" sx={{ fontSize: '0.975rem', fontWeight: '600', borderBottom: 'none' }}>
                            <p className="flex-end">{row.netSalary > 0 ? Utils.formatNumber(parseFloat(row.netSalary).toFixed(2), true, true) : '-'}</p>
                            {row.expensePayments && row.expensePayments.length > 0 && (
                              <p className="flex-end" style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 500, margin: 0 }}>
                                + Exp: {Utils.formatNumber(row.expensePayments.reduce((s: number, e: any) => s + (e.amount || 0), 0).toFixed(2), true, true)}
                              </p>
                            )}
                          </TableCell>

                          <TableCell align="left" className="tableRowCell" sx={{ fontSize: '0.975rem', fontWeight: '400', borderBottom: 'none' }}>
                            <Chip
                              label={row.paymentStatus}
                              size="small"
                              style={{
                                fontSize: '7pt',
                                height: '20px',
                                backgroundColor: row.paymentStatus === 'PAID' ? '#16a34a' : row.paymentStatus === 'PROCESSED' ? '#ea580c' : '#6b7280',
                                color: 'white'
                              }}
                            />
                          </TableCell>

                          <TableCell align="center" className="tableRowCell" sx={{ fontSize: '0.975rem', fontWeight: '400', borderBottom: 'none' }}>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/salary/${row._id}/view`)}
                              color="info"
                              title="View Salary Slip"
                              sx={{ mr: 0.5 }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadSlip(row._id, row.employee?.name || 'Employee')}
                              color="success"
                              title="Download Salary Slip"
                              sx={{ mr: 0.5 }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                            {(row.paymentStatus === 'PENDING' || row.paymentStatus === 'PROCESSED') && (
                              <IconButton
                                size="small"
                                onClick={() => handleMarkAsPaid(row)}
                                color="primary"
                                title="Mark as Paid"
                                sx={{ mr: 0.5 }}
                              >
                                <PaymentIcon fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(row)}
                              color="error"
                              title="Delete"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <StyledTableRow>
                          <StyledTableCell colSpan={9} style={{ textAlign: 'center' }}>No Data Found</StyledTableCell>
                        </StyledTableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
      </Box>
    </Box>

      {/* Salary Form Drawer */}
      <Drawer
        disableEscapeKeyDown={false}
        anchor="right"
        open={openSalaryDrawer}
      >
        <DialogProcessSalaryForm
          title="Process Monthly Salary"
          open={openSalaryDrawer}
          onClose={handleCloseSalary}
        />
      </Drawer>

      {/* Daily Wage Form Drawer */}
      <Drawer
        disableEscapeKeyDown={false}
        anchor="right"
        open={openDailyWageDrawer}
      >
        <DialogProcessDailyWageForm
          title="Process Daily Wage"
          open={openDailyWageDrawer}
          onClose={handleCloseDailyWage}
        />
      </Drawer>

      {/* Modern Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: {
            enter: 400,
            exit: 200,
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
            transform: deleteDialogOpen ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            transition: 'all 0.3s ease-in-out',
          },
          '& .MuiDialog-paper': {
            animation: deleteDialogOpen ? 'slideInUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none',
          },
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(60px) scale(0.9)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0) scale(1)',
              opacity: 1,
            },
          },
        }}
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            backgroundColor: '#fef2f2',
            borderBottom: '1px solid #fecaca',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 3,
            px: 3
          }}
        >
          <Box sx={{
            backgroundColor: '#fee2e2',
            borderRadius: '50%',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DeleteIcon sx={{ color: '#dc2626', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{
              fontWeight: 600,
              color: '#1f2937',
              fontSize: '1.25rem',
              lineHeight: 1.2
            }}>
              Delete {salaryToDelete?.paymentType === 'MONTHLY_SALARY' ? 'Salary' : 'Daily Wage'} Payment
            </Typography>
            <Typography variant="body2" sx={{
              color: '#6b7280',
              mt: 0.5,
              fontSize: '0.875rem'
            }}>
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 4, px: 3 }}>
          <Typography
            variant="body1"
            sx={{
              color: '#1f2937',
              fontSize: '1.125rem',
              fontWeight: 400,
              lineHeight: 1.7,
              letterSpacing: '0.01em',
              pt: 2,
              pb: 1,
              mb: 3
            }}
          >
            Are you sure you want to delete this {salaryToDelete?.paymentType === 'MONTHLY_SALARY' ? 'salary' : 'daily wage'} payment record?
          </Typography>
          {salaryToDelete && (
            <Typography
              variant="body2"
              sx={{
                color: '#6b7280',
                fontSize: '0.95rem',
                lineHeight: 1.6,
                mb: 3
              }}
            >
              Payment Amount: <strong>{Utils.formatNumber(parseFloat(salaryToDelete.netSalary as any) || 0, true, true)}</strong><br />
              Employee: <strong>{salaryToDelete.employee?.name || '-'}</strong>
            </Typography>
          )}

          <Box sx={{
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: 2,
            p: 3,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2
          }}>
            <Box sx={{
              backgroundColor: '#fbbf24',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 24,
              height: 24
            }}>
              <Typography sx={{
                color: '#ffffff',
                fontSize: '0.875rem',
                fontWeight: 'bold'
              }}>
                !
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{
                fontWeight: 600,
                color: '#92400e',
                fontSize: '0.875rem',
                mb: 0.5
              }}>
                Important Warning
              </Typography>
              <Typography variant="body2" sx={{
                color: '#92400e',
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}>
                This salary payment and all associated breakdown data will be permanently removed. This data cannot be recovered once deleted.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          backgroundColor: '#f9fafb',
          borderTop: '1px solid #e5e7eb',
          px: 3,
          py: 2.5,
          gap: 1.5
        }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              minWidth: 100,
              height: 40,
              textTransform: 'none',
              fontWeight: 500,
              borderColor: '#d1d5db',
              color: '#374151',
              '&:hover': {
                borderColor: '#6b7280',
                backgroundColor: '#e5e7eb',
                color: '#1f2937'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
            sx={{
              minWidth: 140,
              height: 40,
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#dc2626',
              '&:hover': {
                backgroundColor: '#b91c1c'
              },
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
            }}
          >
            Delete Payment
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SalaryList;
